import { BellOutlined, CalendarOutlined, LineChartOutlined } from '@ant-design/icons';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { Mail, Smartphone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import AuthIconTooltip from '../../components/auth/AuthIconTooltip';
import ContactEntryDialogs from '../../components/auth/ContactEntryDialogs';
import GoogleIconSignIn from '../../components/auth/GoogleIconSignIn';
import {
    useGenerateEmailOtpMutation,
    useLoginWithGoogleMutation,
    useSendPhoneOtpMutation,
    useVerifyEmailOtpMutation,
    useVerifyPhoneOtpMutation,
} from '../../utils/apiSlice';
import { loginSuccess } from '../../utils/authSlice';
import { runLoginGamification } from '../../utils/gamificationService';
import { hydrateGamification } from '../../utils/gamificationSlice';
import {
    isValidContactEmail,
    isValidContactPhoneDigits,
    type ContactEntryFieldErrors,
} from '../../utils/contactEntryValidation';
import "../VitalModal/VitalModal.scss";
import { triggerCoins } from '../canvasConfict';
import { useNavigate } from "react-router-dom";

const RegisterModal = ({ open, handleClose, activeItem, onConfirm, error }: any) => {
    const navigate = useNavigate();
    const [isExistingUser, setIsExistingUser] = useState(true)
    const dispatch = useDispatch();
    const [loginWithGoogle] = useLoginWithGoogleMutation();
    const [generateEmailOtp] = useGenerateEmailOtpMutation();
    const [verifyEmailOtp] = useVerifyEmailOtpMutation();
    const [sendPhoneOtp] = useSendPhoneOtpMutation();
    const [verifyPhoneOtp] = useVerifyPhoneOtpMutation();
    const [googleLoading, setGoogleLoading] = useState(false);
    const [contactEntryKind, setContactEntryKind] = useState<null | 'email' | 'phone'>(null);
    const [draftFullName, setDraftFullName] = useState("");
    const [draftEmail, setDraftEmail] = useState("");
    const [draftPhone, setDraftPhone] = useState("");
    const [contactFieldErrors, setContactFieldErrors] = useState<ContactEntryFieldErrors>({});

    const clearContactFieldError = (field: "name" | "email" | "phone") => {
        setContactFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    // Email signup OTP (stay on guest chat — no /register redirect)
    const [showEmailOtpModal, setShowEmailOtpModal] = useState(false);
    const [emailOtpDigits, setEmailOtpDigits] = useState(["", "", "", "", "", ""]);
    const [emailHasOtpError, setEmailHasOtpError] = useState(false);
    const [emailOtpCountdown, setEmailOtpCountdown] = useState(0);
    const emailOtpRefs = React.useRef<Array<HTMLInputElement | null>>([]);

    // Phone OTP (popup on top of current screen)
    const [showPhoneOtpModal, setShowPhoneOtpModal] = useState(false);
    const [phoneOtp, setPhoneOtp] = useState(["", "", "", "", "", ""]);
    const [phoneHasOtpError, setPhoneHasOtpError] = useState(false);
    const [phoneCountdown, setPhoneCountdown] = useState(0);
    const phoneOtpRefs = React.useRef<Array<HTMLInputElement | null>>([]);

    const otpBackdropGuest = {
        style: { backgroundColor: "transparent", backdropFilter: "none" as const },
    };

    const isForbidden = (err: any) => {
        const s = err?.status ?? err?.originalStatus ?? err?.data?.statusCode;
        return s === 403 || s === "403";
    };

    const closeContactEntry = () => {
        setContactEntryKind(null);
        setContactFieldErrors({});
        setIsExistingUser(true)
    };

    const persistSessionFromPayload = (payloadData: any, storage: Storage) => {
        const tokenString =
            payloadData?.token?.accessToken ||
            payloadData?.token ||
            payloadData?.data?.token?.accessToken ||
            payloadData?.data?.token;
        const patientProfile =
            payloadData?.patientProfile ||
            payloadData?.data?.patientProfile ||
            payloadData?.user ||
            payloadData?.data?.user ||
            {};
        if (!tokenString) return;

        const normalizedPatientId =
            patientProfile?.patientId ??
            patientProfile?.patientID ??
            patientProfile?.patient_id ??
            payloadData?.patientId ??
            payloadData?.patientID ??
            payloadData?.patient_id ??
            payloadData?.data?.patientId ??
            payloadData?.data?.patientID ??
            payloadData?.data?.patient_id ??
            payloadData?.patientProfileId ??
            payloadData?.data?.patientProfileId ??
            null;

        const mergedProfile =
            normalizedPatientId != null && normalizedPatientId !== ""
                ? { ...patientProfile, patientId: normalizedPatientId }
                : patientProfile;
        const userId =
            mergedProfile?.userId ??
            mergedProfile?.id ??
            payloadData?.userId ??
            payloadData?.id ??
            null;
        const safeRoles = mergedProfile?.role || payloadData?.roles || "USER";
        const familyMembers = payloadData?.familyMembers || [];
        storage.setItem("token", tokenString);
        storage.setItem("userData", JSON.stringify(mergedProfile));
        storage.setItem("familyMembers", JSON.stringify(familyMembers));
        const rolesArray = typeof safeRoles === "string" && safeRoles.includes(",") ? safeRoles.split(",") : [safeRoles];
        storage.setItem("roles", JSON.stringify(rolesArray));
        dispatch(loginSuccess(tokenString));

        if (userId) {
            runLoginGamification(String(userId));
            dispatch(hydrateGamification(String(userId)));
        }
    };

    const handleGoogleCredential = async (idToken: string) => {
        setGoogleLoading(true);
        try {
            const response: any = await loginWithGoogle({ idToken }).unwrap();
            const payloadData = response?.data ?? response;
            if (response?.data?.coinSummary?.coinsEarned) {
                triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)
                const coinsEarned = response?.data?.coinSummary?.coinsEarned || 0;
                const totalCoins = response?.data?.coinSummary?.totalCoins || 0;

                // store as object
                localStorage.setItem(
                    "coinSummary",
                    JSON.stringify({
                        earned: coinsEarned,
                        total: totalCoins,
                    })
                );
            }
            if (response?.success || payloadData?.token || payloadData?.patientProfile) {
                persistSessionFromPayload(payloadData, localStorage);
                toast.success(response?.message || "Login successful");
                handleClose();
                window.location.href = '/dashboard'
                return;
            }
            toast.error(response?.message || "Login Error");
        } catch (err: any) {
            if (err?.originalStatus === 200 || err?.status === 200) {
                const payloadData = err?.data?.data || err?.data || {};
                persistSessionFromPayload(payloadData, localStorage);
                toast.success(err?.data?.message || "Login successful");
                handleClose();
                window.location.href = '/dashboard'
                return;
            }
            let errorMsg = "Google sign-in failed";
            if (typeof err?.data === "string") {
                errorMsg = err.data;
            } else if (err?.data?.message) {
                errorMsg = err.data.message;
            } else if (err?.data?.detail) {
                errorMsg = err.data.detail;
            } else if (err?.error === "TypeError: Failed to fetch") {
                errorMsg = "Network Error: Failed to connect to the server.";
            }
            toast.error(errorMsg);
        } finally {
            setGoogleLoading(false);
        }
    };

    const openEmailEntry = () => {
        setContactFieldErrors({});
        setDraftFullName("");
        setDraftEmail("");
        setContactEntryKind("email");
    };

    const openPhoneEntry = () => {
        setContactFieldErrors({});
        setDraftFullName("");
        setDraftPhone("");
        setContactEntryKind("phone");
    };

    const getOtpCountdownSeconds = (resp: any, fallbackSeconds = 30) => {
        const rawSeconds =
            resp?.expiresInSeconds ??
            resp?.data?.expiresInSeconds ??
            resp?.expires_in_seconds ??
            resp?.data?.expires_in_seconds;

        const rawMinutes =
            resp?.expiresInMinutes ??
            resp?.data?.expiresInMinutes ??
            resp?.expires_in_minutes ??
            resp?.data?.expires_in_minutes;

        const seconds = typeof rawSeconds === "string" ? parseInt(rawSeconds, 10) : rawSeconds;
        if (typeof seconds === "number" && seconds > 0) return Math.min(seconds, fallbackSeconds);

        const minutes = typeof rawMinutes === "string" ? parseFloat(rawMinutes) : rawMinutes;
        if (typeof minutes === "number" && minutes > 0) return Math.min(Math.floor(minutes * 60), fallbackSeconds);

        return fallbackSeconds;
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins < 10 ? "0" + mins : mins}:${secs < 10 ? "0" + secs : secs}`;
    };

    const submitEmailEntry = async () => {
        const fullName = String(draftFullName || "").trim();
        const e = String(draftEmail).trim();
        const next: ContactEntryFieldErrors = {};
        if (!fullName && !isExistingUser) next.name = "Enter your full name.";
        if (!isValidContactEmail(e)) next.email = "Enter a valid email address.";
        if (Object.keys(next).length) {
            setContactFieldErrors(next);
            return;
        }
        setContactFieldErrors({});
        try {
            const resp: any = await generateEmailOtp({ email: e }).unwrap();
            closeContactEntry();
            setEmailHasOtpError(false);
            setEmailOtpDigits(["", "", "", "", "", ""]);
            setEmailOtpCountdown(getOtpCountdownSeconds(resp, 30));
            setShowEmailOtpModal(true);
            toast.info(resp?.message || "OTP sent to your email");
        } catch (error: any) {
            if (isForbidden(error)) return;
            let msg = "Failed to send OTP.";
            if (typeof error?.data === "string") msg = error.data;
            else if (error?.data?.message) msg = error.data.message;
            else if (error?.data?.detail) msg = error.data.detail;
            toast.error(msg);
        }
    };

    const submitPhoneEntry = async () => {
        const fullName = String(draftFullName || "").trim();
        const digits = draftPhone.replace(/\D/g, "").slice(0, 10);
        const next: ContactEntryFieldErrors = {};
        if (!fullName && !isExistingUser) next.name = "Enter your full name.";
        if (!isValidContactPhoneDigits(digits)) {
            next.phone = "Enter a valid 10-digit mobile number (starts with 6–9).";
        }
        if (Object.keys(next).length) {
            setContactFieldErrors(next);
            return;
        }
        setContactFieldErrors({});
        const e164 = `+91${digits}`;
        try {
            const resp: any = await sendPhoneOtp({ phoneNumber: e164 }).unwrap();
            closeContactEntry();
            setPhoneHasOtpError(false);
            setPhoneOtp(["", "", "", "", "", ""]);
            setPhoneCountdown(getOtpCountdownSeconds(resp, 30));
            setShowPhoneOtpModal(true);
            toast.info(resp?.message || "OTP sent to your mobile number");
        } catch (error: any) {


            if (isForbidden(error)) return;

            if (error?.status === 500) {
                closeContactEntry();
                setShowPhoneOtpModal(true);
                setPhoneCountdown(getOtpCountdownSeconds(error?.data ?? error, 30));
                setPhoneHasOtpError(false);
                return;
            }
            let msg = "Failed to send OTP.";
            if (typeof error?.data === "string") msg = error.data;
            else if (error?.data?.message) msg = error.data.message;
            else if (error?.data?.detail) msg = error.data.detail;
            const normalized = String(msg || "").toLowerCase();
            if (normalized.includes("otp already sent") || normalized.includes("otp already verified")) {
                closeContactEntry();
                setPhoneHasOtpError(false);
                setPhoneOtp(["", "", "", "", "", ""]);
                setPhoneCountdown(getOtpCountdownSeconds(error?.data ?? error, 30));
                setShowPhoneOtpModal(true);
                return;
            }
            toast.error(msg);
        }
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showPhoneOtpModal && phoneCountdown > 0) {
            timer = setInterval(() => setPhoneCountdown((p) => p - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [phoneCountdown, showPhoneOtpModal]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showEmailOtpModal && emailOtpCountdown > 0) {
            timer = setInterval(() => setEmailOtpCountdown((p) => p - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [emailOtpCountdown, showEmailOtpModal]);



    const handleChangePhoneOtp = (value: string, index: number) => {
        if (isNaN(Number(value))) return;

        setPhoneHasOtpError(false);

        const next = [...phoneOtp];
        next[index] = value.substring(value.length - 1);
        setPhoneOtp(next);

        // Move focus
        if (value && index < 5) {
            phoneOtpRefs.current[index + 1]?.focus();
        }


        const otpValue = next.join("");
        if (otpValue.length === 6 && !next.includes("")) {
            void handleVerifyPhoneOtpLogin(otpValue);
        }
    };

    const handleKeyDownPhoneOtp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !phoneOtp[index] && index > 0) {
            phoneOtpRefs.current[index - 1]?.focus();
        }
    };



    const handleVerifyPhoneOtpLogin = async (otpValueArg?: string) => {
        const otpValue = otpValueArg ?? phoneOtp.join("");
        if (otpValue.length < 6) {
            setPhoneHasOtpError(true);
            toast.error("Please enter the complete OTP");
            return;
        }
        const digits = draftPhone.replace(/\D/g, "").slice(0, 10);
        const e164 = `+91${digits}`;
        const name = String(draftFullName || "").trim();
        // if (!name) {
        //     toast.error("Please enter your full name");
        //     return;
        // }
        try {
            const response: any = await verifyPhoneOtp({ phoneNumber: e164, otp: otpValue, name }).unwrap();
            const payloadData = response?.data ?? response;
            const tokenString = payloadData?.token?.accessToken || payloadData?.token;
            if (response?.data?.coinSummary?.coinsEarned) {
                triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)
                const coinsEarned = response?.data?.coinSummary?.coinsEarned || 0;
                const totalCoins = response?.data?.coinSummary?.totalCoins || 0;

                // store as object
                localStorage.setItem(
                    "coinSummary",
                    JSON.stringify({
                        earned: coinsEarned,
                        total: totalCoins,
                    })
                );
            }

            if (tokenString) {
                persistSessionFromPayload(payloadData, localStorage);
                toast.success(response?.message || "Login successful");
                setShowPhoneOtpModal(false);
                handleClose();
                window.location.href = '/dashboard'
                return;
            }
            toast.success(response?.message || "OTP verified");
            setShowPhoneOtpModal(false);
        } catch (error: any) {
            if (error?.originalStatus === 200 || error?.status === 200 || error?.status === 202) {
                const payloadData = error?.data?.data ?? error?.data ?? {};
                const tokenString = payloadData?.token?.accessToken || payloadData?.token;
                if (tokenString) {
                    persistSessionFromPayload(payloadData, localStorage);
                    toast.success(error?.data?.message || "Login successful");
                    setShowPhoneOtpModal(false);
                    handleClose();
                    window.location.href = '/dashboard'
                    return;
                }
                toast.success(error?.data?.message || "OTP verified");
                setShowPhoneOtpModal(false);
                return;
            }
            if (error?.originalStatus === 500 || error?.status === 500) {
                setPhoneHasOtpError(true);
                return
            }
            if (isForbidden(error)) return;
            setPhoneHasOtpError(true);
            let msg = "Invalid OTP. Please try again.";
            if (typeof error?.data === "string") msg = error.data;
            else if (error?.data?.message) msg = error.data.message;
            else if (error?.data?.detail) msg = error.data.detail;
            toast.error(msg);
        }
    };

    const handleResendPhoneOtp = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (phoneCountdown !== 0) return;
        const digits = draftPhone.replace(/\D/g, "").slice(0, 10);
        const e164 = `+91${digits}`;
        try {
            const resp: any = await sendPhoneOtp({ phoneNumber: e164 }).unwrap();
            setPhoneCountdown(getOtpCountdownSeconds(resp, 30));
            toast.info(resp?.message || "OTP has been resent!");
        } catch (error: any) {
            if (isForbidden(error)) return;
            let msg = "Failed to resend OTP.";
            if (typeof error?.data === "string") msg = error.data;
            else if (error?.data?.message) msg = error.data.message;
            else if (error?.data?.detail) msg = error.data.detail;
            toast.error(msg);
        }
    };

    const handleChangeEmailOtp = (value: string, index: number) => {
        if (isNaN(Number(value))) return;
        setEmailHasOtpError(false);
        const next = [...emailOtpDigits];
        next[index] = value.slice(-1);
        setEmailOtpDigits(next);
        if (value && index < 5) {
            emailOtpRefs.current[index + 1]?.focus();
        }
        const otpValue = next.join("");
        if (otpValue.length === 6 && !next.includes("")) {
            void handleVerifyEmailOtpSignup(otpValue);
        }
    };

    const handleKeyDownEmailOtp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !emailOtpDigits[index] && index > 0) {
            emailOtpRefs.current[index - 1]?.focus();
        }
    };

    const handleVerifyEmailOtpSignup = async (otpValue: string) => {
        const emailAddr = String(draftEmail || "").trim();
        const name = String(draftFullName || "").trim();
        if (otpValue.length < 6) {
            setEmailHasOtpError(true);
            toast.error("Please enter the complete OTP");
            return;
        }

        try {
            const response: any = await verifyEmailOtp({
                email: emailAddr,
                otp: otpValue,
                username: name,
            }).unwrap();
            toast.success(response?.message || "OTP Verified successfully!");
            const payloadData = response?.data ?? response;
            const tokenString = payloadData?.token?.accessToken || payloadData?.token;

            if (response?.data?.coinSummary?.coinsEarned) {
                triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)
                const coinsEarned = response?.data?.coinSummary?.coinsEarned || 0;
                const totalCoins = response?.data?.coinSummary?.totalCoins || 0;

                // store as object
                localStorage.setItem(
                    "coinSummary",
                    JSON.stringify({
                        earned: coinsEarned,
                        total: totalCoins,
                    })
                );
            }
            if (tokenString) {
                persistSessionFromPayload(payloadData, localStorage);
                setShowEmailOtpModal(false);
                handleClose();
                window.location.href = "/dashboard";
                return;
            }
            setShowEmailOtpModal(false);
            window.location.href = "/dashboard";
        } catch (error: any) {
            if (error?.originalStatus === 200 || error?.status === 200 || error?.status === 202) {
                toast.success(error?.data?.message || "OTP Verified successfully!");
                const payloadData = error?.data?.data ?? error?.data ?? {};
                const tokenString = payloadData?.token?.accessToken || payloadData?.token;
                if (tokenString) {
                    persistSessionFromPayload(payloadData, localStorage);
                    setShowEmailOtpModal(false);
                    handleClose();
                    window.location.href = "/dashboard";
                    return;
                }
                setShowEmailOtpModal(false);
                window.location.href = "/dashboard";
                return;
            }
            if (error?.originalStatus === 500 || error?.status === 500) {
                setEmailHasOtpError(true);
                return
            }

            if (isForbidden(error)) return;
            setEmailHasOtpError(true);
            let msg = "Invalid OTP. Please try again.";
            if (typeof error?.data === "string") msg = error.data;
            else if (error?.data?.message) msg = error.data.message;
            else if (error?.data?.detail) msg = error.data.detail;
            toast.error(msg);
        }
    };

    const handleResendEmailOtp = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (emailOtpCountdown !== 0) return;
        const emailAddr = String(draftEmail || "").trim();
        if (!isValidContactEmail(emailAddr)) {
            toast.error("Invalid email.");
            return;
        }
        try {
            const resp: any = await generateEmailOtp({ email: emailAddr }).unwrap();
            setEmailOtpCountdown(getOtpCountdownSeconds(resp, 30));
            setEmailOtpDigits(["", "", "", "", "", ""]);
            setEmailHasOtpError(false);
            toast.info(resp?.message || "OTP has been resent!");
        } catch (error: any) {
            if (isForbidden(error)) return;
            let msg = "Failed to resend OTP.";
            if (typeof error?.data === "string") msg = error.data;
            else if (error?.data?.message) msg = error.data.message;
            else if (error?.data?.detail) msg = error.data.detail;
            toast.error(msg);
        }
    };

    useEffect(() => {
        if (open) {

        }
    }, [open, activeItem?.key]);



    const renderContent = () => {
        return (
            <Box sx={{ textAlign: 'center' }} >

                <div  >
                    <div className={`vitals-container `}>
                        {/* Progress Tracking */}
                        <div className="vitals-row" onClick={() => navigate("/register")}>
                            <div className="vitals-icon-wrapper tracking-bg">
                                <LineChartOutlined className="vitals-icon" />
                            </div>
                            <div className="vitals-text-content">
                                <h6 className="vitals-title">Progress Tracking</h6>
                                <p className="vitals-desc">Visual charts for your health vitals.</p>
                            </div>
                        </div>

                        {/* Medication Alerts */}
                        <div className="vitals-row" onClick={() => navigate("/register")}>
                            <div className="vitals-icon-wrapper alert-bg">
                                <BellOutlined className="vitals-icon" />
                            </div>
                            <div className="vitals-text-content">
                                <h6 className="vitals-title">Medication Alerts</h6>
                                <p className="vitals-desc">Smart reminders, never miss a dose.</p>
                            </div>
                        </div>

                        {/* Doctor Appointments */}
                        <div className="vitals-row" onClick={() => navigate("/register")}>
                            <div className="vitals-icon-wrapper calendar-bg">
                                <CalendarOutlined className="vitals-icon" />
                            </div>
                            <div className="vitals-text-content">
                                <h6 className="vitals-title">Doctor Appointments</h6>
                                <p className="vitals-desc">Book & manage sessions instantly.</p>
                            </div>
                        </div>
                    </div>
                    {/* 
                    <div className="divider-container my-4">
                        <div className="divider-line"></div>
                        <span className="divider-text">OR</span>
                        <div className="divider-line"></div>
                    </div> */}
                    <div className="d-flex justify-content-center align-items-center gap-3 mt-4 mb-2 auth-oauth-icon-row">
                        <AuthIconTooltip label="Continue With Google">
                            <GoogleIconSignIn
                                text="signin_with"
                                disabled={googleLoading}
                                onCredential={handleGoogleCredential}
                                onError={() => toast.error("Google sign-in was cancelled or failed.")}
                            />
                        </AuthIconTooltip>
                        <AuthIconTooltip label="Continue With Email">
                            <button
                                type="button"
                                className="btn btn-outline-light shadow-sm rounded-circle p-3 d-flex align-items-center justify-content-center icon-login-btn"
                                onClick={openEmailEntry}
                            >
                                <Mail size={24} className="text-primary" />
                            </button>
                        </AuthIconTooltip>
                        <AuthIconTooltip label="Continue With Phone Number">
                            <button
                                type="button"
                                className="btn btn-outline-light shadow-sm rounded-circle p-3 d-flex align-items-center justify-content-center icon-login-btn"
                                onClick={openPhoneEntry}
                            >
                                <Smartphone size={24} className="text-success" />
                            </button>
                        </AuthIconTooltip>
                    </div>


                    {/* Footer Trust Signal */}
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, bgcolor: '#22c55e', borderRadius: '50%' }} />
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                            SECURE ENCRYPTED ACCESS
                        </Typography>
                    </Box>

                </div>
                {error && (
                    <div className="gesture-overlay">
                        <div className="hand-wrapper">
                            <AdsClickIcon
                                sx={{
                                    fontSize: 50,
                                    color: '#5156be ',
                                    filter: 'drop-shadow(0px 4px 10px rgba(255, 77, 79, 0.4))'
                                }}
                                className="movable-hand"
                            />
                        </div>
                        <h4 className="action-title" style={{ color: "#5156be" }}>Please Login To Continue</h4>
                    </div>
                )}
            </Box>
        );
    };


    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 543,
                    borderRadius: "20px",
                    padding: "10px",
                    background: "#fff",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    margin: "20px auto"
                }}

            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 2
                    }}

                >
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#2d3748" }}
                    >
                        Unlock Full Health insights
                    </Typography>


                </Box>

                {/* Content */}
                <Box sx={{ p: '0.5% 1% 0 4%' }} className={` ${error ? 'login-box-error' : ''} `}>
                    {renderContent()}
                </Box>
            </Box>
            <ContactEntryDialogs
                kind={contactEntryKind}
                subtleBackdrop
                onClose={closeContactEntry}
                draftFullName={draftFullName}
                onDraftFullNameChange={setDraftFullName}
                draftEmail={draftEmail}
                onDraftEmailChange={setDraftEmail}
                draftPhone={draftPhone}
                onDraftPhoneChange={setDraftPhone}
                fieldErrors={contactFieldErrors}
                onClearContactFieldError={clearContactFieldError}
                onEmailNext={submitEmailEntry}
                onPhoneNext={submitPhoneEntry}
                checkUser={setIsExistingUser}
                isExistingUser={isExistingUser}
            />

            <Dialog
                open={showEmailOtpModal}
                onClose={() => setShowEmailOtpModal(false)}
                maxWidth="xs"
                fullWidth
                className="auth-modal glass-modal"
                BackdropProps={otpBackdropGuest}
            >
                <IconButton
                    className="auth-modal-round-close"
                    aria-label="close"
                    onClick={() => setShowEmailOtpModal(false)}
                >
                    <CloseIcon />
                </IconButton>
                <DialogTitle className="text-center pt-30 pb-10">
                    <h2>Enter Security Code</h2>
                    <p className="text-muted fs-14 mb-0">Code sent to {String(draftEmail || "").trim()}</p>
                </DialogTitle>
                <DialogContent className="px-40 pb-30">
                    <div className="mt-3 text-center form-input-container">
                        <div className="otp-container d-flex justify-content-between mb-30" style={{ gap: "10px" }}>
                            {emailOtpDigits.map((digit, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength={1}
                                    value={digit}
                                    ref={(el) => {
                                        emailOtpRefs.current[index] = el;
                                    }}
                                    onChange={(e) => handleChangeEmailOtp(e.target.value, index)}
                                    onKeyDown={(e) => handleKeyDownEmailOtp(e, index)}
                                    className={`form-control otp-input ${digit ? "filled" : ""} ${emailHasOtpError ? "border-danger" : ""}`}
                                />
                            ))}
                        </div>
                        <div className="auth-otp-resend mt-30">
                            <p className="auth-otp-resend__hint">Didn&apos;t receive the code?</p>
                            {emailOtpCountdown > 0 ? (
                                <div className="auth-otp-resend__timer" role="status" aria-live="polite" aria-atomic="true">
                                    <span className="auth-otp-resend__timer-label">Resend in</span>
                                    <span className="auth-otp-resend__timer-value">{formatTime(emailOtpCountdown)}</span>
                                </div>
                            ) : (
                                <a href="#" onClick={handleResendEmailOtp} className="auth-otp-resend__link">
                                    Resend code
                                </a>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showPhoneOtpModal}
                onClose={() => setShowPhoneOtpModal(false)}
                maxWidth="xs"
                fullWidth
                className="auth-modal glass-modal"
                BackdropProps={otpBackdropGuest}
            >
                <IconButton
                    className="auth-modal-round-close"
                    aria-label="close"
                    onClick={() => setShowPhoneOtpModal(false)}
                >
                    <CloseIcon />
                </IconButton>
                <DialogTitle className="text-center pt-30 pb-10">
                    <h2>Enter Security Code</h2>
                    <p className="text-muted fs-14 mb-0">Code sent to +91{draftPhone.replace(/\D/g, "").slice(0, 10)}</p>
                </DialogTitle>
                <DialogContent className="px-40 pb-30">
                    <div className="mt-3 text-center form-input-container">
                        <div className="otp-container d-flex justify-content-between mb-30" style={{ gap: "10px" }}>
                            {phoneOtp.map((digit, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength={1}
                                    value={digit}
                                    ref={(el) => {
                                        phoneOtpRefs.current[index] = el;
                                    }}
                                    onChange={(e) => handleChangePhoneOtp(e.target.value, index)}
                                    onKeyDown={(e) => handleKeyDownPhoneOtp(e, index)}
                                    className={`form-control otp-input ${digit ? "filled" : ""} ${phoneHasOtpError ? "border-danger" : ""}`}
                                />
                            ))}
                        </div>

                        <div className="text-center mt-30">
                            <p className="mb-1 text-muted">Didn't receive the code?</p>
                            {phoneCountdown > 0 ? (
                                <span className="text-danger fw-bold fs-16">
                                    Resend in {formatTime(phoneCountdown)}
                                </span>
                            ) : (
                                <a href="#" onClick={handleResendPhoneOtp} className="text-primary fw-bold fs-16">
                                    Resend Now
                                </a>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RegisterModal;